import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClubs, createEvent, getUser, getEvents } from '../services/api';
import toast from 'react-hot-toast';
import ClubSearch from '../components/shared/ClubSearch';

/**
 * CreateEvent - Página para crear un nuevo pozo
 * 
 * Formulario para que los organizadores den de alta nuevos eventos.
 */
const CreateEvent = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [clubs, setClubs] = useState([]);
    const organizerId = localStorage.getItem('userId');
    
    // Permission States
    const [canCreate, setCanCreate] = useState(true);
    const [blockReason, setBlockReason] = useState(null);
    const [userPermissions, setUserPermissions] = useState(null);

    // Estado del formulario
    const [formData, setFormData] = useState({
        title: '',
        clubId: '',
        date: '',
        startTime: '',
        levelMin: '3.0',
        levelMax: '4.0',
        pricePerPlayer: '10',
        maxPairs: '16',
        numberOfCourts: '4',
        cancellationDeadlineHours: '24',
        description: '',
        posterUrl: '',
        clubName: '',
        // Nuevos campos de modalidad
        gameMode: 'gold_court', // 'gold_court' o 'micro_times'
        totalDuration: '120',  // Minutos totales
        roundDuration: '20'    // Minutos por turno
    });

    // Validar permisos y cargar datos
    useEffect(() => {
        const checkPermissions = async () => {
            try {
                if (!organizerId) return;

                // 1. Get User Profile & Permissions
                const user = await getUser(organizerId);
                if (!user) {
                    toast.error("Error al cargar perfil de usuario");
                    return;
                }

                // Check Subscription Status
                if (user.subscriptionStatus === 'blocked') {
                    setCanCreate(false);
                    setBlockReason("Tu cuenta está bloqueada por impago. Contacta con el soporte.");
                    toast.error("Cuenta bloqueada: No puedes crear eventos.");
                    return;
                }

                const maxEvents = user.permissions?.maxEvents === 'unlimited' ? Infinity : (user.permissions?.maxEvents || 1);
                const allowedTypes = user.permissions?.allowedTypes || [];

                // Check Allowed Types (Assuming this page is for 'pozo')
                if (!allowedTypes.includes('pozo')) {
                    setCanCreate(false);
                    setBlockReason("Tu plan actual no permite crear Pozos.");
                    return;
                }

                // 2. Count Active Events
                // Published or In Progress
                const { events } = await getEvents({ organizerId });
                const activeEvents = events.filter(e => e.status === 'published' || e.status === 'in_progress');
                
                if (activeEvents.length >= maxEvents) {
                    setCanCreate(false);
                    setBlockReason(`Has alcanzado el límite de eventos activos para tu plan (${activeEvents.length}/${maxEvents}).`);
                    toast.error(`Límite de eventos alcanzado (${activeEvents.length}/${maxEvents})`);
                }

                setUserPermissions(user.permissions);

            } catch (error) {
                console.error("Error checking permissions:", error);
            }
        };

        const fetchClubs = async () => {
            try {
                const data = await getClubs({ organizerId });
                // data.clubs viene de la paginación del backend o array directo dependiendo de la respuesta
                // Ajustar según la estructura real de respuesta de getClubs
                const clubsList = data.clubs || data;
                setClubs(clubsList);
                if (clubsList.length > 0) {
                    setFormData(prev => ({ 
                        ...prev, 
                        clubId: clubsList[0].id,
                        clubName: clubsList[0].name 
                    }));
                }
            } catch (error) {
                console.error('Error cargando clubes:', error);
                toast.error('Error al cargar la lista de clubes');
            }
        };

        checkPermissions();
        fetchClubs();
    }, [organizerId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        setFormData(prev => {
            const newState = { ...prev, [name]: value };
            
            // Auto-calcular parejas y jugadores si cambia el número de pistas
            if (name === 'numberOfCourts') {
                const courts = parseInt(value) || 0;
                newState.maxPairs = (courts * 2).toString();
            }
            
            return newState;
        });
    };

    const handleClubSelect = (club) => {
        if (!club) {
            setFormData(prev => ({
                ...prev,
                clubId: '',
                clubName: ''
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            clubId: club.id || '', // Si tiene ID (de nuestra DB), se usa. Si no, se deja vacío para que se use clubName
            clubName: club.n || club.name || ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Validaciones básicas antes de enviar
            if (!formData.clubId) {
                throw new Error('Debes seleccionar un club');
            }

            const userId = localStorage.getItem('userId');
            const selectedClub = clubs.find(c => c.id === formData.clubId);

            const payload = {
                ...formData,
                organizerId: userId,
                status: 'published',
                currentPairs: 0,
                type: 'pozo',
                club: selectedClub ? {
                    id: selectedClub.id,
                    name: selectedClub.name,
                    city: selectedClub.city,
                    latitude: selectedClub.latitude,
                    longitude: selectedClub.longitude
                } : null,
                levelMin: parseFloat(formData.levelMin),
                levelMax: parseFloat(formData.levelMax),
                pricePerPlayer: parseFloat(formData.pricePerPlayer),
                maxPairs: parseInt(formData.maxPairs),
                numberOfCourts: parseInt(formData.numberOfCourts),
                cancellationDeadlineHours: parseInt(formData.cancellationDeadlineHours),
                // Asegurar tipos para los nuevos campos
                totalDuration: parseInt(formData.totalDuration),
                roundDuration: parseInt(formData.roundDuration),
            };

            await createEvent(payload);
            toast.success('¡Pozo creado exitosamente!');
            navigate('/dashboard/events');
        } catch (error) {
            console.error('Error creando evento:', error);
            const msg = error.response?.data?.message || error.message || 'Error al crear el pozo';

            if (error.response?.status === 401 || error.response?.status === 403) {
                toast.error('No tienes permisos. Por favor inicia sesión como organizador.');
                // Opcional: navigate('/login');
            } else {
                toast.error(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    {/* Header */}
                    <div className={`${!canCreate ? 'bg-red-600' : 'bg-primary-600'} px-8 py-6 transition-colors`}>
                        <h2 className="text-3xl font-bold text-white font-display">
                            Crear Nuevo Pozo
                        </h2>
                        <p className={`mt-2 ${!canCreate ? 'text-red-100 font-bold' : 'text-primary-100'}`}>
                            {!canCreate ? `⛔ ${blockReason}` : 'Configura los detalles de tu nuevo torneo'}
                        </p>

                        {!canCreate && (
                            <div className="mt-6 flex flex-wrap gap-3 animate-fade-in-up">
                                <button 
                                    onClick={() => navigate('/pricing')}
                                    type="button"
                                    className="px-5 py-2.5 bg-white text-red-600 rounded-xl font-black text-sm hover:bg-red-50 transition-all shadow-lg transform hover:-translate-y-0.5 flex items-center gap-2"
                                >
                                    <span>💎</span>
                                    SUBIR DE PLAN
                                </button>
                                <button 
                                    onClick={() => navigate('/dashboard')}
                                    type="button"
                                    className="px-5 py-2.5 bg-red-800/50 text-white rounded-xl font-bold text-sm hover:bg-red-800 transition-all border border-red-500/30 flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Cerrar
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Formulario */}
                    <form onSubmit={handleSubmit} className={`p-8 space-y-6 ${!canCreate ? 'opacity-50 pointer-events-none' : ''}`}>

                        {/* Título */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Título del Pozo
                            </label>
                            <input
                                type="text"
                                name="title"
                                required
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Ej: Pozo Viernes Noche"
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-3 px-4 border"
                            />
                        </div>

                        {/* Club */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Club de Pádel
                            </label>
                            <ClubSearch 
                                onSelect={handleClubSelect} 
                                initialValue={formData.clubName}
                                placeholder="Escribe el nombre del club..."
                                organizerId={organizerId}
                            />
                            <p className="text-[10px] text-gray-400 mt-1 italic">
                                * Selecciona un club de la lista o escribe uno nuevo.
                            </p>
                        </div>

                        {/* Fecha y Hora (Grid) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Fecha
                                </label>
                                <input
                                    type="date"
                                    name="date"
                                    required
                                    value={formData.date}
                                    onChange={handleChange}
                                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-3 px-4 border"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Hora de Inicio
                                </label>
                                <input
                                    type="time"
                                    name="startTime"
                                    required
                                    value={formData.startTime}
                                    onChange={handleChange}
                                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-3 px-4 border"
                                />
                            </div>
                        </div>

                        {/* Niveles (Grid) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nivel Mínimo (1.0 - 7.0)
                                </label>
                                <input
                                    type="number"
                                    step="0.5"
                                    min="1"
                                    max="7"
                                    name="levelMin"
                                    required
                                    value={formData.levelMin}
                                    onChange={handleChange}
                                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-3 px-4 border"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nivel Máximo (1.0 - 7.0)
                                </label>
                                <input
                                    type="number"
                                    step="0.5"
                                    min="1"
                                    max="7"
                                    name="levelMax"
                                    required
                                    value={formData.levelMax}
                                    onChange={handleChange}
                                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-3 px-4 border"
                                />
                            </div>
                        </div>

                        {/* Detalles Logísticos (Pistas, Parejas, Jugadores) */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1 uppercase tracking-tight">
                                    Pistas Reservadas
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    name="numberOfCourts"
                                    required
                                    value={formData.numberOfCourts}
                                    onChange={handleChange}
                                    className="w-full rounded-lg border-primary-500 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-3 px-4 border-2 font-black text-lg text-primary-700 bg-white"
                                />
                                <p className="text-[10px] text-primary-600 mt-1 italic font-bold">
                                    * Define el aforo automáticamente
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-400 mb-1 uppercase tracking-tight">
                                    Máximo de Parejas
                                </label>
                                <input
                                    type="number"
                                    name="maxPairs"
                                    readOnly
                                    value={formData.maxPairs}
                                    className="w-full rounded-lg border-gray-200 bg-gray-50 py-3 px-4 border font-bold text-gray-400 cursor-not-allowed"
                                />
                                <p className="text-[10px] text-gray-400 mt-1 italic">
                                    Bloqueado (2 por pista)
                                </p>
                            </div>
                            <div className="bg-primary-600 rounded-lg p-3 border border-primary-700 flex flex-col justify-center items-center shadow-lg transform hover:scale-105 transition-transform cursor-help" title="Cálculo: Pistas x 4 jugadores">
                                <span className="text-[10px] font-black text-primary-100 uppercase tracking-widest">Total Jugadores</span>
                                <span className="text-3xl font-black text-white italic">
                                    {parseInt(formData.numberOfCourts || 0) * 4}
                                </span>
                                <span className="text-[9px] text-primary-200 font-bold">Capacidad Máxima</span>
                            </div>
                        </div>

                        {/* Precio por Jugador */}
                        <div className="pt-4 border-t border-gray-100">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Precio por Jugador (€)
                            </label>
                            <div className="relative mt-1 max-w-xs">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500 sm:text-sm">€</span>
                                </div>
                                <input
                                    type="number"
                                    min="0"
                                    name="pricePerPlayer"
                                    required
                                    value={formData.pricePerPlayer}
                                    onChange={handleChange}
                                    className="block w-full pl-7 pr-12 rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500 py-3 px-4 border"
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        {/* Cancellation Policy */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Plazo de cancelación (horas antes del evento)
                            </label>
                            <input
                                type="number"
                                min="0"
                                name="cancellationDeadlineHours"
                                required
                                value={formData.cancellationDeadlineHours || 24}
                                onChange={handleChange}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-3 px-4 border"
                                placeholder="24"
                            />
                            <p className="text-xs text-gray-400 mt-1">
                                Los jugadores no podrán darse de baja si faltan menos de estas horas para el inicio.
                            </p>
                        </div>

                        {/* Descripción */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Descripción (Opcional)
                            </label>
                            <textarea
                                name="description"
                                rows="3"
                                value={formData.description}
                                onChange={handleChange}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-3 px-4 border"
                                placeholder="Detalles adicionales sobre el formato del torneo..."
                            />
                        </div>

                        {/* CONFIGURACIÓN DE MODALIDAD (POZO) */}
                        <div className="bg-primary-50 p-6 rounded-2xl border border-primary-100 space-y-6">
                            <h3 className="text-lg font-bold text-primary-900 flex items-center gap-2">
                                ⚙️ Configuración del Modo de Juego
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Selector de Modo */}
                                <div className="space-y-3">
                                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">
                                        Modalidad de Pozo
                                    </label>
                                    <div className="grid grid-cols-1 gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setFormData(p => ({ ...p, gameMode: 'gold_court' }))}
                                            className={`p-4 rounded-xl border-2 text-left transition-all ${formData.gameMode === 'gold_court' ? 'border-primary-500 bg-white shadow-md' : 'border-gray-200 bg-gray-50 opacity-60'}`}
                                        >
                                            <p className="font-bold text-gray-900">🏆 Pista Líder (Corte por Set)</p>
                                            <p className="text-xs text-gray-500 mt-1">La primera pista que llegue a 6 juegos para el juego y rotan.</p>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(p => ({ ...p, gameMode: 'micro_times' }))}
                                            className={`p-4 rounded-xl border-2 text-left transition-all ${formData.gameMode === 'micro_times' ? 'border-primary-500 bg-white shadow-md' : 'border-gray-200 bg-gray-50 opacity-60'}`}
                                        >
                                            <p className="font-bold text-gray-900">⏱️ Micro-tiempos</p>
                                            <p className="text-xs text-gray-500 mt-1">Rondas de tiempo fijo (ej: 20 min) para todos los partidos.</p>
                                        </button>
                                    </div>
                                </div>

                                {/* Ajustes específicos */}
                                <div className="space-y-4">
                                    {/* Duración Total: Siempre visible */}
                                    <div className="p-4 bg-white rounded-xl border border-primary-100">
                                        <label className="block text-sm font-bold text-gray-700 mb-2">
                                            Duración Total del Pozo: {Math.floor(formData.totalDuration / 60)}h {formData.totalDuration % 60}min
                                        </label>
                                        <input
                                            type="range"
                                            name="totalDuration"
                                            min="30"
                                            max="300"
                                            step="15"
                                            value={formData.totalDuration}
                                            onChange={handleChange}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                                        />
                                        <div className="flex justify-between text-[10px] text-gray-400 mt-1 font-bold">
                                            <span>30min</span>
                                            <span>5 horas</span>
                                        </div>
                                    </div>

                                    {/* Ajustes de Turno: Solo en micro-tiempos */}
                                    {formData.gameMode === 'micro_times' && (
                                        <div className="p-4 bg-white rounded-xl border border-primary-100">
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Tiempo por Turno (minutos)
                                            </label>
                                            <input
                                                type="number"
                                                name="roundDuration"
                                                min="1"
                                                max="60"
                                                value={formData.roundDuration}
                                                onChange={handleChange}
                                                className="w-full rounded-lg border-gray-300 focus:border-primary-500 py-2 border px-3"
                                            />
                                            <p className="text-xs text-primary-600 mt-2 font-medium italic">
                                                * Mínimo 1 minuto para pruebas.
                                            </p>
                                        </div>
                                    )}

                                    <p className="text-[10px] text-gray-400 font-medium leading-relaxed italic px-2">
                                        {formData.gameMode === 'gold_court' 
                                            ? "* Avisos acústicos al inicio y fin del pozo." 
                                            : "* Música motivadora al inicio y en cada cambio de turno."}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Cartel Publicitario (Poster) */}
                        <div className="bg-gray-50 p-6 rounded-xl border-2 border-dashed border-gray-300">
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                                🖼️ Cartel Publicitario / Poster del Evento
                            </label>
                            <div className="flex flex-col md:flex-row gap-6 items-center">
                                <div className="flex-1 w-full">
                                    <p className="text-xs text-gray-500 mb-4">
                                        Sube una imagen promocional para atraer a más jugadores. Aparecerá de forma destacada en la tarjeta del pozo.
                                    </p>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    setFormData(prev => ({ ...prev, posterUrl: reader.result }));
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        className="block w-full text-sm text-gray-500
                                            file:mr-4 file:py-2 file:px-4
                                            file:rounded-full file:border-0
                                            file:text-sm file:font-semibold
                                            file:bg-primary-50 file:text-primary-700
                                            hover:file:bg-primary-100 cursor-pointer"
                                    />
                                </div>
                                {formData.posterUrl && (
                                    <div className="w-32 h-32 relative group">
                                        <img
                                            src={formData.posterUrl}
                                            alt="Preview"
                                            className="w-full h-full object-cover rounded-lg shadow-md border-2 border-white"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, posterUrl: '' }))}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600 transition-colors"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Botones de acción */}
                        <div className="flex items-center justify-end gap-4 pt-6 md:pt-8 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => navigate('/events')}
                                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`
                                    px-8 py-3 bg-primary-600 text-white rounded-lg font-bold shadow-lg 
                                    hover:bg-primary-700 transition-all transform focus:ring-4 focus:ring-primary-200
                                    ${loading ? 'opacity-70 cursor-wait' : 'hover:-translate-y-0.5'}
                                `}
                            >
                                {loading ? 'Creando pozo...' : '🚀 Publicar Pozo'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateEvent;
